import React from 'react';
import { Button } from '@roger/ui-components';
import type {
  ExternalEmrListStatusFilter,
  ExternalEmrStatusCounts,
} from '../../../services/externalEmrPatients';
import { STATUS_FILTERS, statusChipLabel } from './externalEmrPatientCopy';

interface ExternalEmrStatusChipsProps {
  counts: ExternalEmrStatusCounts | null;
  selected: ExternalEmrListStatusFilter | null;
  onSelect: (status: ExternalEmrListStatusFilter | null) => void;
  /** Take Over v2 is available to the viewer: shows "Needs Take Over". */
  canTakeOver: boolean;
}

/**
 * The External EMR table's status filter (EMR-2915), like the A–Z row: one
 * toggle per status with its count. Pressing the selected chip clears it. A
 * chip that would list nothing is disabled, unless it is the one selected.
 */
export const ExternalEmrStatusChips: React.FC<ExternalEmrStatusChipsProps> = ({
  counts,
  selected,
  onSelect,
  canTakeOver,
}) => {
  if (!counts) return null;
  return (
    <div
      className='mb-2 flex flex-wrap gap-1'
      role='group'
      aria-label='Filter by status'
      data-testid='external-emr-status-chips'
    >
      {STATUS_FILTERS.filter(
        status => canTakeOver || status !== 'needs_take_over'
      ).map(status => {
        const count = counts[status] ?? 0;
        const isSelected = selected === status;
        return (
          <Button
            key={status}
            variant={isSelected ? 'default' : 'outline'}
            size='sm'
            className='whitespace-nowrap'
            aria-pressed={isSelected}
            disabled={count === 0 && !isSelected}
            onClick={() => onSelect(isSelected ? null : status)}
            data-testid={`external-emr-status-${status}`}
          >
            {statusChipLabel(status, count)}
          </Button>
        );
      })}
    </div>
  );
};
